import type { CpgRetailer } from '@features/campaigns/subdomains/cpg';
import { MAX_RETAILER_COUNT } from '@features/campaigns/subdomains/cpg';
import type {
  BorderProps,
  Slide,
  TableCell,
  TableRow,
} from '@features/powerpoint';
import { formatters } from '@utils/formattersV2';
import { formatters as cpgFormatters } from '@features/campaigns/subdomains/cpg';
import { sanitizeOptimization } from '@utils/valueTransformers';
import { themeV2 } from '../../../../domain/theme-v2';
import { DEFAULT_FONT } from '../../../../utils/constants-v2';
import { contentSlideHeaderV2 } from '../../../../utils/slide-header-v2';
import { textStylesV2 } from '../../../../utils/text-styles-v2';

const NO_BORDER: BorderProps = { type: 'none' };
const BOTTOM_BORDER: BorderProps = {
  type: 'solid',
  color: themeV2.colors.magnetBlue,
  pt: 1,
};

const generateRow = (cells: TableCell[], showBottomBorder = true): TableRow =>
  cells.map(cell => ({
    text: cell.text,
    options: {
      valign: 'middle' as const,
      fontFace: DEFAULT_FONT,
      fill: { type: 'none' as const },
      border: [
        NO_BORDER,
        NO_BORDER,
        showBottomBorder ? BOTTOM_BORDER : NO_BORDER,
        NO_BORDER,
      ],
      ...cell.options,
    },
  }));

type MinimalCpgRetailer = Pick<
  CpgRetailer,
  | 'name'
  | 'projectedIncrementalSales'
  | 'incrementalSalesLift'
  | 'totalProjectedSales'
  | 'averagePricePerTransaction'
  | 'penetration'
  | 'averagePurchaseFrequency'
  | 'salesRate'
  | 'averageTotalBasketAmount'
  | 'salesLiftIndex'
>;

type TableField = {
  columnLabel: string;
  getValue: (retailer: MinimalCpgRetailer) => string;
  key: keyof MinimalCpgRetailer;
};

const tableFields = [
  {
    columnLabel: 'Retailer',
    getValue: retailer => retailer.name,
    key: 'name',
  },
  {
    columnLabel: 'Projected Incr. Sales',
    getValue: retailer =>
      formatters.currency(retailer.projectedIncrementalSales, {
        full: true,
        decimals: 0,
      }),
    key: 'projectedIncrementalSales',
  },
  {
    columnLabel: 'Incremental Sales Lift',
    getValue: retailer =>
      formatters.lift(retailer.incrementalSalesLift, { decimals: 1 }),
    key: 'incrementalSalesLift',
  },
  {
    columnLabel: 'Total Projected Sales',
    getValue: retailer =>
      formatters.currency(retailer.totalProjectedSales, {
        full: true,
        decimals: 0,
      }),
    key: 'totalProjectedSales',
  },
  {
    columnLabel: 'Avg. Price per Transaction',
    getValue: retailer =>
      formatters.currency(retailer.averagePricePerTransaction),
    key: 'averagePricePerTransaction',
  },
  {
    columnLabel: 'Penetration',
    getValue: retailer =>
      formatters.percentage(retailer.penetration, { decimals: 2 }),
    key: 'penetration',
  },
  {
    columnLabel: 'Avg. Purchase Frequency',
    getValue: retailer =>
      cpgFormatters.formatAveragePurchaseFrequency(
        retailer.averagePurchaseFrequency
      ),
    key: 'averagePurchaseFrequency',
  },
  {
    columnLabel: 'Sales Rate',
    getValue: retailer => formatters.currency(retailer.salesRate),
    key: 'salesRate',
  },
  {
    columnLabel: 'Avg. Total Basket Amount',
    getValue: retailer =>
      formatters.currency(retailer.averageTotalBasketAmount),
    key: 'averageTotalBasketAmount',
  },
  {
    columnLabel: 'Optimization Index',
    getValue: retailer =>
      formatters.number(sanitizeOptimization(retailer.salesLiftIndex), {
        decimals: 2,
      }),
    key: 'salesLiftIndex',
  },
] as const satisfies readonly TableField[];

const TABLE_HEADER_OPTIONS: TableCell['options'] = {
  color: themeV2.colors.metricBlue,
  bold: true,
  fontFace: DEFAULT_FONT,
};

export type PerformanceByTopRetailersSlideV2Props = {
  slideMaster: string;
  retailers: MinimalCpgRetailer[];
  excludedColumns: string[];
  page: number;
  totalPages: number;
};

export const performanceByTopRetailersSlideV2 = (
  props: PerformanceByTopRetailersSlideV2Props
): Slide => {
  const filteredTableFields = tableFields.filter(
    field => !props.excludedColumns.includes(field.key)
  );

  const columnHeaders: TableCell[] = filteredTableFields.map(field => ({
    text: field.columnLabel,
    options: TABLE_HEADER_OPTIONS,
  }));

  const dataRows = props.retailers.map((retailer, index) => {
    const isLastRow = index === props.retailers.length - 1;
    return generateRow(
      filteredTableFields.map(field => ({ text: field.getValue(retailer) })),
      !isLastRow
    );
  });

  return {
    slideMaster: props.slideMaster,
    tables: [
      {
        tableRows: [generateRow(columnHeaders), ...dataRows],
        options: {
          x: 0.36,
          y: 1.17,
          w: 12.6,
          h: 1.22,
          rowH: 0.51,
          fontSize: 10,
          ...textStylesV2.contentSlideDefaults,
        },
      },
    ],
    texts: [
      contentSlideHeaderV2(
        `Performance by Top ${MAX_RETAILER_COUNT} Retailers (${props.page}/${props.totalPages})`
      ),
    ],
  };
};
